import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PointCloudNodeFromData,
  type PointCloudNodeFromDataProps,
} from '../PointCloudNodeFromData';
import { calcScale, makeVisibilityBoxUniforms, PointCloudMaterial } from '../PointCloudMaterial';
import { ColorMap } from '../ColorMap';
import { type BinPoints, defaultMaxId } from '../Loaders/BinPointsLoader';
import { BenchmarkMesh, type BenchmarkMeshProps, TRIANGLES_PER_MESH } from './BenchmarkMesh';
import { PointsAttributeBuffer } from '../PointsAttributeBuffer';
import { useEdlRenderer } from '../EyeDomeLighting';
import { CALLBACK_ORDER_WEBGL_BENCHMARK } from '../FrameCallbackOrder';
import { useVisibilityBox } from '../useVisibilityBox';

export interface WebGlBenchmarkResult {
  numTriangles: number;
  numPoints: number;
  recomendedPointBudget: number;
  trianglesFrameTime: number;
  frameTime: number;
  maxFrameTime: number;
  frameTimes: number[];
  benchmarkTime: number;
  isCompleted: boolean;
  screen: { width: number; height: number };
  gpuInfo: { vendor: string; renderer: string };
}

const EXPECTED_TIME_MSEC = 30_000;

const START_NUM_TRIANGLES = 10_000;
const MAX_NUM_TRIANGLES = 100_000;
const MAX_NUM_TRIANGLES_PER_STEP = 25_000;
const MAX_TRIANGLES_FRAME_TIME_MSEC = 40;

const START_NUM_POINTS = 500_000;
const MAX_NUM_POINTS = 100_000_000;
const MAX_NUM_POINTS_PER_STEP = 2_500_000;
const NUM_POINTS_PER_NODE = 50_000;

const COMPLEXITY_INCREASE_STEP = 1.618;
const MAX_FRAME_TIME_MSEC = 50;
const FRAME_RERENDER_COUNT = 10;
const FRAME_RERENDER_SKIP_COUNT = 3;

const MAX_WORST_FRAME_TIME_MSEC = 1000;

const RECOMENDED_POINTS_RATIO = 0.8;

const CAMERA_TARGET_POSITION = [10, 10, 10];
const CAMERA_TARGET_SIZE = [25, 25, 25];

const INVALIDATE_FRAME_COUNT = FRAME_RERENDER_COUNT;

export interface WebGlBenchmarkInnerProps {
  onProgress: (percent: number, intermediateResult: WebGlBenchmarkResult) => void;
  onComplete: (result: WebGlBenchmarkResult) => void;
}

export const WebGlBenchmarkInner = ({ onProgress, onComplete }: WebGlBenchmarkInnerProps) => {
  const colorMapReference = useRef(new ColorMap(defaultMaxId));
  const colorMap = colorMapReference.current;

  const gl = useThree((state) => state.gl);
  const scene = useThree((state) => state.scene);
  const camera = useThree((state) => state.camera);
  const invalidate = useThree((state) => state.invalidate);

  const enableEDL = true;
  const edlStrength = 1;
  const edlRenderer = useEdlRenderer({
    gl,
    scene,
    camera,
    enableEDL,
    edlStrength,
    invalidateFrame: invalidate,
  });

  const vboxUniformsReference = useRef(makeVisibilityBoxUniforms());
  const visibilityBoxUniforms = vboxUniformsReference.current;

  const pointCloudMaterial = useMemo(() => {
    return edlRenderer
      ? new PointCloudMaterial(
          { colorMap, attributeName: 'annotation', invalidateFrame: invalidate, hasColor: true },
          visibilityBoxUniforms,
          edlRenderer,
          calcScale(
            gl.domElement.height,
            camera instanceof THREE.PerspectiveCamera ? camera.fov : 50
          )
        )
      : undefined;
  }, [edlRenderer, colorMap, invalidate, visibilityBoxUniforms, gl.domElement.height, camera]);

  const pointCloudMaterialMap = useMemo(() => {
    return new Map<string, PointCloudMaterial>(
      pointCloudMaterial ? [['annotation', pointCloudMaterial]] : []
    );
  }, [pointCloudMaterial]);

  useVisibilityBox({
    smallVisibilityBoxOnParent: undefined,
    setSmallVisibilityBoxOnParent: undefined,
    visibilityBoxOnParent: undefined,
    setVisibilityBoxOnParent: undefined,
    vboxUniformsReference,
    pointCloudMaterialMap,
    materials: undefined,
  });

  useEffect(() => {
    return () => {
      if (pointCloudMaterial) pointCloudMaterial.dispose();
    };
  }, [pointCloudMaterial]);

  const getPointCloudMaterial = useCallback(
    (material: string): PointCloudMaterial => {
      if (!pointCloudMaterial || material !== 'annotation') {
        throw new Error('unexpected material name');
      }
      return pointCloudMaterial;
    },
    [pointCloudMaterial]
  );

  const sceneComplexityReference = useRef<{ numPoints: number; numTriangles: number }>({
    numTriangles: 0,
    numPoints: 0,
  });

  const [meshesProps, setMeshesProps] = useState<BenchmarkMeshProps[]>([]);
  const [pointCloudsProps, setPointCloudsProps] = useState<PointCloudNodeFromDataProps[]>([]);

  const addMesh = useCallback(() => {
    const px = Math.random();
    const py = Math.random();
    const pz = Math.random();
    const rx = Math.random();
    const ry = Math.random();
    const rz = Math.random();

    const [tx, ty, tz] = CAMERA_TARGET_POSITION;
    const [sx, sy, sz] = CAMERA_TARGET_SIZE;

    setMeshesProps((previous: BenchmarkMeshProps[]) => {
      const meshProps: BenchmarkMeshProps = {
        key: `mesh-${previous.length}`,
        position: [tx + sx * px, ty + sy * py, tz + sz * pz] as Parameters<THREE.Vector3['set']>,
        rotation: [rx, ry, rz] as Parameters<THREE.Vector3['set']>,
      };
      return [...previous, meshProps];
    });
  }, []);

  const addPoints = useCallback(() => {
    const makeRandomArray = (
      numberPoints: number,
      cardinality: number,
      offset: number[],
      scale: number[]
    ) => {
      const array = new Float32Array(numberPoints * cardinality);
      for (let pointIndex = 0; pointIndex < numberPoints; pointIndex += 1) {
        const point = [...offset];
        for (let index = 0; index < cardinality; index += 1) {
          point[index] += scale[index] * Math.random();
        }
        array.set(point, pointIndex * cardinality);
      }
      return array;
    };

    const numberPoints = NUM_POINTS_PER_NODE;

    const positionCardinality = 3;
    const colorCardinality = 3;

    const positionArray = makeRandomArray(
      numberPoints,
      positionCardinality,
      CAMERA_TARGET_POSITION,
      CAMERA_TARGET_SIZE
    );
    const colorArray = makeRandomArray(numberPoints, colorCardinality, [0, 0, 0], [1, 1, 1]);

    const annotationBuffer = new PointsAttributeBuffer(defaultMaxId, numberPoints);
    for (let pointIndex = 0; pointIndex < numberPoints; pointIndex += 1) {
      annotationBuffer.setValue(pointIndex, Math.round(Math.random() * defaultMaxId));
    }

    const positionData = new THREE.Float32BufferAttribute(positionArray, positionCardinality);
    const colorData = new THREE.Float32BufferAttribute(colorArray, colorCardinality);
    const annotationData = new THREE.Float32BufferAttribute(
      annotationBuffer.data,
      annotationBuffer.cardinality
    );
    positionData.needsUpdate = true;
    annotationData.needsUpdate = true;

    const attributes = [
      {
        name: 'annotation',
        data: annotationData,
        attributeBuffer: annotationBuffer,
        isColor: false,
      },
      {
        name: 'color',
        data: colorData,
        isColor: true,
      },
    ];

    const binPoints: BinPoints = { positionData, attributes };

    setPointCloudsProps((previous: PointCloudNodeFromDataProps[]) => {
      const name = `points-${previous.length}`;
      const nodeProps: PointCloudNodeFromDataProps = {
        octreeId: name,
        binPoints,
        name,
        url: name,
        material: 'annotation',
        getPointCloudMaterial,
      };
      return [...previous, nodeProps];
    });
  }, [getPointCloudMaterial]);

  const resultReference = useRef<WebGlBenchmarkResult>({
    numPoints: 0,
    numTriangles: 0,
    recomendedPointBudget: 0,
    trianglesFrameTime: 0,
    frameTime: 0,
    maxFrameTime: 0,
    frameTimes: [],
    isCompleted: false,
    benchmarkTime: 0,
    screen: { height: window.screen.height, width: window.screen.width },
    gpuInfo: { vendor: 'unknown', renderer: 'unknown' },
  });

  const insreaseSceneComplexity = useCallback(() => {
    const { numTriangles, numPoints } = sceneComplexityReference.current;
    if (
      numTriangles < MAX_NUM_TRIANGLES &&
      resultReference.current.frameTime < MAX_TRIANGLES_FRAME_TIME_MSEC
    ) {
      const nextNumberTriangles =
        numTriangles === 0
          ? START_NUM_TRIANGLES
          : Math.min(
              Math.floor(numTriangles * COMPLEXITY_INCREASE_STEP),
              MAX_NUM_TRIANGLES,
              numTriangles + MAX_NUM_TRIANGLES_PER_STEP
            );
      let currentTriangles = numTriangles;
      for (; currentTriangles < nextNumberTriangles; currentTriangles += TRIANGLES_PER_MESH) {
        addMesh();
      }
      sceneComplexityReference.current.numTriangles = currentTriangles;
      return true;
    }
    if (resultReference.current.trianglesFrameTime === 0) {
      resultReference.current.trianglesFrameTime = resultReference.current.frameTime;
    }
    if (numPoints < MAX_NUM_POINTS) {
      const { trianglesFrameTime, frameTime } = resultReference.current;
      const pointsTime = frameTime - trianglesFrameTime;
      const remainingTime = MAX_FRAME_TIME_MSEC - frameTime;
      const maxStepToTargetFrameTime = Math.max(
        0.125 * MAX_NUM_POINTS_PER_STEP,
        pointsTime <= 0 || remainingTime <= 0 ? 0 : (remainingTime / pointsTime) * numPoints
      );
      const nextNumberPoints =
        numPoints === 0
          ? START_NUM_POINTS
          : Math.min(
              Math.floor(numPoints * COMPLEXITY_INCREASE_STEP),
              MAX_NUM_POINTS,
              numPoints + MAX_NUM_POINTS_PER_STEP,
              numPoints + maxStepToTargetFrameTime
            );
      let currentPoints = numPoints;
      for (; currentPoints < nextNumberPoints; currentPoints += NUM_POINTS_PER_NODE) {
        addPoints();
      }
      sceneComplexityReference.current.numPoints = currentPoints;
      return true;
    }
    return false;
  }, [addMesh, addPoints]);

  const meshesComp = useMemo(
    () =>
      meshesProps.map((meshProps) => {
        const { key } = meshProps;
        return <BenchmarkMesh {...meshProps} key={key} />;
      }),
    [meshesProps]
  );

  const simplePointCloudsComp = useMemo(
    () =>
      pointCloudsProps.map((spcProps) => {
        const { url } = spcProps;
        return (
          <PointCloudNodeFromData
            {...spcProps}
            getPointCloudMaterial={getPointCloudMaterial}
            key={url}
          />
        );
      }),
    [pointCloudsProps, getPointCloudMaterial]
  );

  const percent = useRef<number>(0);

  const isCompletedReference = useRef<boolean>(false);

  const callOnComplete = useCallback(() => {
    const { numPoints, frameTime } = resultReference.current;
    resultReference.current = {
      ...resultReference.current,
      recomendedPointBudget:
        Math.round(
          (numPoints * RECOMENDED_POINTS_RATIO * MAX_FRAME_TIME_MSEC) / frameTime / 100_000
        ) * 100_000,
      isCompleted: true,
    };
    if (percent.current < 100) {
      percent.current = 100;
      onProgress(percent.current, resultReference.current);
    }
    isCompletedReference.current = true;
    onComplete(resultReference.current);
  }, [onProgress, onComplete]);

  useEffect(() => {
    if (camera) {
      camera.position.set(0, 0, 0);
      camera.lookAt(new THREE.Vector3(...CAMERA_TARGET_POSITION));
      camera.updateProjectionMatrix();
    }
  }, [camera]);

  useEffect(() => {
    const context = gl.getContext();
    const extension = context.getExtension('WEBGL_debug_renderer_info');
    if (extension) {
      const vendor = context.getParameter(extension?.UNMASKED_VENDOR_WEBGL);
      const renderer = context.getParameter(extension?.UNMASKED_RENDERER_WEBGL);
      resultReference.current.gpuInfo = {
        vendor,
        renderer,
      };
    }
  }, [gl]);

  const currentStepReference = useRef<{ frameTimes: number[] }>({
    frameTimes: [],
  });

  const benchmarkStartTime = useRef<number>(performance.now());

  const [renderTarget, pixelBuffer] = useMemo(() => {
    const { width, height } = window.screen;
    return [new THREE.WebGLRenderTarget(width, height), new Uint8Array(4 * width * height)];
  }, []);

  const doMeasurementStep = useCallback(() => {
    if (isCompletedReference.current) return;

    const renderer = gl;

    const { frameTimes } = currentStepReference.current;

    // move camera to ensure rerendering of new view
    const shift = Math.cos((frameTimes.length / FRAME_RERENDER_COUNT) * Math.PI) * 2;

    camera.position.set(shift, shift, shift);
    camera.updateProjectionMatrix();

    // render the scene
    const startTime = performance.now();

    renderer.autoClear = true;
    renderer.setRenderTarget(renderTarget);
    renderer.render(scene, camera);
    renderer.setRenderTarget(null);

    // rendering is not happening without readRenderTargetPixels
    // using smaller size to reduce GPU->CPU copying time effect
    renderer.readRenderTargetPixels(
      renderTarget,
      0, // x
      0, // y
      20,
      20,
      //   renderTarget.width, // width
      //   renderTarget.height, // height
      pixelBuffer
    );

    const elapsedTimeMsec = performance.now() - startTime;

    frameTimes.push(elapsedTimeMsec);

    const updatePercent = () => {
      const benchmarkTime = performance.now() - benchmarkStartTime.current;
      const completedPart = Math.min(1, resultReference.current.frameTime / MAX_FRAME_TIME_MSEC);
      const timePart = Math.min(1, benchmarkTime / EXPECTED_TIME_MSEC);
      const k2 = 0.75 * completedPart;
      const currentPercent = 100 * ((1 - k2) * timePart + k2 * completedPart);

      const k = 1 / FRAME_RERENDER_COUNT;
      percent.current = Math.max(
        percent.current,
        Math.min(100, percent.current * (1 - k) + currentPercent * k)
      );
    };

    if (frameTimes.length < FRAME_RERENDER_COUNT) {
      updatePercent();
      onProgress(percent.current, resultReference.current);
      invalidate(INVALIDATE_FRAME_COUNT);
      return;
    }

    const maxFrameTime = frameTimes.reduce((total: number, value: number) => {
      return Math.max(total, value);
    }, 0);
    // exclude staring frames, which can be affected by extra loading time
    const avgFrameTimes = frameTimes.slice(FRAME_RERENDER_SKIP_COUNT);
    const avgFrameTime =
      avgFrameTimes.reduce((total: number, value: number) => {
        return total + value;
      }, 0) / avgFrameTimes.length;

    const { numPoints, numTriangles } = sceneComplexityReference.current;

    resultReference.current = {
      ...resultReference.current,
      numPoints,
      numTriangles,
      frameTime: avgFrameTime,
      maxFrameTime,
      frameTimes: [...frameTimes],
      isCompleted: false,
    };

    currentStepReference.current.frameTimes = [];

    if (
      avgFrameTime < MAX_FRAME_TIME_MSEC &&
      maxFrameTime < MAX_WORST_FRAME_TIME_MSEC &&
      insreaseSceneComplexity()
    ) {
      updatePercent();
      onProgress(percent.current, resultReference.current);
      invalidate(INVALIDATE_FRAME_COUNT);
      return;
    }

    callOnComplete();
  }, [
    camera,
    gl,
    scene,
    invalidate,
    renderTarget,
    pixelBuffer,
    onProgress,
    callOnComplete,
    insreaseSceneComplexity,
  ]);

  useFrame(() => {
    doMeasurementStep();
  }, CALLBACK_ORDER_WEBGL_BENCHMARK);

  return (
    <group>
      <group>{meshesComp}</group>
      <group>{simplePointCloudsComp}</group>
    </group>
  );
};
WebGlBenchmarkInner.displayName = 'WebGlBenchmarkInner';
